import { isHttpError } from '@breezeventures/web-lib-ph-request/errors';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  getIsOrderConfirmationSessionValid,
  orderConfirmationSessionAtom,
  setOrderConfirmationSessionAtom,
} from './order-confirmation-session-atoms';
import type {
  InitiateConfirmationResponse,
  ResendConfirmationResponse,
} from './types';

import { merchantConfirmationApi } from '../api/merchant-confirmation-api';

export type InitiateOrResendOrderConfirmationVariables = {
  orderId: string;
};

export type UseInitiateOrResendOrderConfirmationOptions = {
  onSuccess?: () => void;
};

function isInitiateConfirmationResponse(
  data: InitiateConfirmationResponse | ResendConfirmationResponse
): data is InitiateConfirmationResponse {
  return 'challenge' in data && 'sessionId' in data;
}

export const useInitiateOrResendOrderConfirmation = ({
  onSuccess,
}: UseInitiateOrResendOrderConfirmationOptions) => {
  const orderSession = useAtomValue(orderConfirmationSessionAtom);
  const setOrderConfirmationSession = useSetAtom(
    setOrderConfirmationSessionAtom
  );

  return useMutation<
    InitiateConfirmationResponse | ResendConfirmationResponse,
    Error,
    InitiateOrResendOrderConfirmationVariables
  >({
    mutationFn: async ({ orderId }) => {
      if (
        !orderSession ||
        !getIsOrderConfirmationSessionValid(orderSession, orderId)
      ) {
        return merchantConfirmationApi.initiateConfirmation({ orderId });
      }

      try {
        const response = await merchantConfirmationApi.resendOrderConfirmation({
          sessionId: orderSession.sessionId,
        });

        return response;
      } catch (error) {
        if (
          error instanceof Error &&
          isHttpError(error) &&
          error.status === 400
        ) {
          return merchantConfirmationApi.initiateConfirmation({ orderId });
        }
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      if (isInitiateConfirmationResponse(data)) {
        setOrderConfirmationSession({
          orderId: variables.orderId,
          data,
        });
        onSuccess?.();
        return;
      }

      if (orderSession) {
        setOrderConfirmationSession({
          orderId: orderSession.orderId,
          data: {
            sessionId: orderSession.sessionId,
            challenge: orderSession.challenge,
          },
        });
        onSuccess?.();
        return;
      }
    },
  });
};
